import { UserRound } from 'lucide-react';

import { cn } from '@/utils/cn';

/**
 * A person's directory photo — passport / ID framing, not a circular profile avatar.
 *
 * Churches use these on membership cards and printed directories. A round crop reads as
 * a social-media icon; this stays rectangular and portrait so the face fills the frame
 * the way a passport picture does.
 */

export type MemberPhotoSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZES: Record<MemberPhotoSize, string> = {
  sm: 'h-10 w-[30px]',
  md: 'h-16 w-12',
  lg: 'h-28 w-[84px]',
  xl: 'h-36 w-[108px]',
  '2xl': 'h-52 w-[156px]',
};

export interface MemberPhotoProps {
  src?: string | null;
  /** Used for alt text and the empty-state label. */
  name: string;
  size?: MemberPhotoSize;
  className?: string;
}

export const MemberPhoto = ({ src, name, size = 'md', className }: MemberPhotoProps) => {
  const label = size === 'lg' || size === 'xl' || size === '2xl' ? initials(name) : '';

  return (
    <div
      className={cn(
        'relative shrink-0 overflow-hidden rounded-sm border border-border bg-surface-sunken',
        'aspect-[3/4]',
        SIZES[size],
        className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={name.trim() || undefined}
          className="h-full w-full object-cover object-center"
        />
      ) : (
        <div
          className="flex h-full w-full flex-col items-center justify-center gap-1 text-fg-tertiary"
          aria-hidden
        >
          <UserRound
            className={cn(
              size === 'sm' || size === 'md' ? 'size-4' : size === '2xl' ? 'size-10' : 'size-8',
              'opacity-70',
            )}
          />
          {label ? (
            <span className="px-1 text-center text-caption leading-tight">{label}</span>
          ) : null}
        </div>
      )}
    </div>
  );
};

/** Initials from a real name only — empty when nothing has been entered yet. */
const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};
